import { useDeleteShippingLocationGroupMutation } from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import Action from '@/gf/components/Action'
import CloseModalButton from '@/gf/components/CloseModalButton'
import Modal from '@/gf/components/ModalNext'
import useMsgs from '@/gf/hooks/useMsgs'
import { ApolloError } from '@apollo/client'
import { useState } from 'react'
import type { LocationGroup } from './index'

const DeleteLocationGroupModal = ({
  locationGroup,
  onClose,
  onSuccess,
}: {
  locationGroup: LocationGroup
  onClose: () => void
  onSuccess: () => void
}) => {
  const client = useGqlClient()
  const [_, msgr] = useMsgs()
  const [deleteGroup] = useDeleteShippingLocationGroupMutation({ client })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleDelete = async () => {
    setIsSubmitting(true)

    try {
      await deleteGroup({
        variables: {
          id: locationGroup.id,
        },
      })

      onSuccess()
    } catch (err) {
      const error = err as ApolloError
      const message = error.graphQLErrors.reduce((acc, error) => `${acc}${error.message}`, '')
      msgr.add(message || 'Failed to delete location group', 'negative')
      setIsSubmitting(false)
    }
  }

  return (
    <Modal onClose={onClose}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Delete Location Group</h3>
          <CloseModalButton onClick={onClose} />
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-600">
            Are you sure you want to delete <strong>{locationGroup.name}</strong>?
          </p>
          <p className="mt-2 text-sm text-gray-600">
            Users in this group will be moved to "No Location Group". This action cannot be undone.
          </p>
        </div>

        <div className="flex justify-end gap-3">
          <Action.S onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Action.S>
          <Action.P
            className="bg-red-600 hover:bg-red-700 border-red-700 hover:border-red-700"
            onClick={handleDelete}
            performing={isSubmitting}
            disabled={isSubmitting}
          >
            Delete
          </Action.P>
        </div>
      </div>
    </Modal>
  )
}

export default DeleteLocationGroupModal
